import React, { useEffect, useMemo, useState } from "react";

/** Avoid huge base64 strings in memory; icon fallback above this (still uploads normally). */
const MAX_DATA_URL_PREVIEW_BYTES = 6 * 1024 * 1024;

const PREVIEWABLE_IMAGE_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
]);

const PREVIEWABLE_EXT = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp"]);

function fileExtension(name) {
  const s = String(name ?? "");
  const i = s.lastIndexOf(".");
  return i >= 0 ? s.slice(i + 1).toLowerCase() : "";
}

function isLikelyUnsupportedImageMime(mime) {
  if (!mime.startsWith("image/")) return false;
  return (
    mime.includes("heic") ||
    mime.includes("heif") ||
    mime.includes("tiff") ||
    mime.includes("svg") ||
    mime === "image/vnd.microsoft.icon"
  );
}

/** Browser can usually render these in <img> from a data: or blob: URL. */
export function getImagePreviewKind(file) {
  if (!file) return "none";
  const isBlobLike = typeof Blob !== "undefined" && file instanceof Blob;
  if (!isBlobLike) return "none";
  const mime = (file.type || "").toLowerCase();
  const ext = fileExtension(file.name);

  if (mime.startsWith("image/")) {
    if (isLikelyUnsupportedImageMime(mime)) return "none";
    if (PREVIEWABLE_IMAGE_MIME.has(mime)) return "image";
    if (PREVIEWABLE_EXT.has(ext)) return "image";
    return "none";
  }
  if (PREVIEWABLE_EXT.has(ext) && (!mime || mime === "application/octet-stream")) {
    return "image";
  }
  return "none";
}

/** Icon tile for PDF / Word / generic file (and failed image decode). */
export function getDocumentIconKind(file) {
  if (!file) return "generic";
  const isBlobLike = typeof Blob !== "undefined" && file instanceof Blob;
  const mimeRaw =
    !isBlobLike && typeof file === "object"
      ? file.type ?? file.contentType ?? file.mimeType ?? ""
      : file.type ?? "";
  const mime = String(mimeRaw || "").toLowerCase();
  const ext = fileExtension(file.name);
  if (mime === "application/pdf" || ext === "pdf") return "pdf";
  if (
    ext === "doc" ||
    ext === "docx" ||
    mime.includes("wordprocessingml") ||
    mime.includes("msword")
  ) {
    return "word";
  }
  return "generic";
}

const PdfGlyph = () => (
  <svg viewBox="0 0 40 48" width="40" height="48" aria-hidden>
    <path
      fill="#E74C3C"
      d="M8 4h18l10 10v30a4 4 0 01-4 4H8a4 4 0 01-4-4V8a4 4 0 014-4z"
    />
    <path fill="#fff" d="M26 4v10h10L26 4z" opacity="0.35" />
    <text
      x="20"
      y="34"
      textAnchor="middle"
      fill="#fff"
      fontSize="14"
      fontWeight="700"
      fontFamily="system-ui,sans-serif"
    >
      PDF
    </text>
  </svg>
);

const WordGlyph = () => (
  <svg viewBox="0 0 40 48" width="40" height="48" aria-hidden>
    <path
      fill="#2B579A"
      d="M8 4h18l10 10v30a4 4 0 01-4 4H8a4 4 0 01-4-4V8a4 4 0 014-4z"
    />
    <path fill="#fff" d="M26 4v10h10L26 4z" opacity="0.25" />
    <text
      x="20"
      y="34"
      textAnchor="middle"
      fill="#fff"
      fontSize="11"
      fontWeight="700"
      fontFamily="system-ui,sans-serif"
    >
      DOC
    </text>
  </svg>
);

const GenericGlyph = () => (
  <svg viewBox="0 0 40 48" width="40" height="48" aria-hidden>
    <path
      fill="#6B7280"
      d="M8 4h18l10 10v30a4 4 0 01-4 4H8a4 4 0 01-4-4V8a4 4 0 014-4z"
    />
    <path fill="#fff" d="M26 4v10h10L26 4z" opacity="0.2" />
    <path
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      d="M12 28h16M12 34h12"
    />
  </svg>
);

function IconByKind({ kind }) {
  if (kind === "pdf") return <PdfGlyph />;
  if (kind === "word") return <WordGlyph />;
  return <GenericGlyph />;
}

/**
 * Thumbnail for local File: real preview for common image formats; PDF/DOC/other as icon + name.
 *
 * Uses data: URLs for image preview (FileReader) instead of blob: URLs so previews work under
 * strict Content-Security-Policy img-src (many Liferay / *.lfr.cloud setups allow data: but not blob:).
 */
function metaObjectImageSrc(file) {
  if (
    !file ||
    typeof file !== "object" ||
    (typeof Blob !== "undefined" && file instanceof Blob)
  ) {
    return null;
  }
  const data = file.previewDataUrl;
  if (typeof data === "string" && data.startsWith("data:image")) return data;
  const remote =
    file.previewUrl ||
    file.documentUrl ||
    file.url ||
    file.fileUrl ||
    file.downloadUrl ||
    file.viewUrl ||
    file.thumbnailUrl ||
    file.imageUrl;
  if (typeof remote === "string" && remote.trim()) {
    const u = remote.trim();
    if (u.startsWith("data:image")) return u;
    // Do not load PDF / non-image URLs in <img> — causes broken preview or odd flashes.
    const ext = fileExtension(file.name || "");
    const mime = String(
      file.type || file.contentType || file.mimeType || "",
    ).toLowerCase();
    const looksImage =
      PREVIEWABLE_EXT.has(ext) ||
      /\.(jpe?g|png|gif|webp|bmp)$/i.test(String(file.name || "")) ||
      (mime.startsWith("image/") &&
        !mime.includes("svg") &&
        !mime.includes("heic") &&
        !mime.includes("heif"));
    if (looksImage && (u.startsWith("blob:") || /^https?:\/\//i.test(u))) {
      return u;
    }
  }
  return null;
}

export default function DocumentFilePreview({ file, className = "" }) {
  const [imgError, setImgError] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);
  const [previewBlocked, setPreviewBlocked] = useState(false);
  const [metaImgFailed, setMetaImgFailed] = useState(false);

  const isBlobLike = typeof Blob !== "undefined" && file instanceof Blob;
  const metaSrc = useMemo(
    () => (!isBlobLike ? metaObjectImageSrc(file) : null),
    [file, isBlobLike],
  );

  useEffect(() => {
    setMetaImgFailed(false);
  }, [metaSrc]);

  const imageKind = useMemo(() => {
    if (!isBlobLike) return "none";
    return getImagePreviewKind(file);
  }, [file, isBlobLike]);
  const iconKind = useMemo(() => getDocumentIconKind(file), [file]);

  useEffect(() => {
    setImgError(false);
    setDataUrl(null);
    setPreviewBlocked(false);

    if (imageKind !== "image" || !file || !isBlobLike) {
      return undefined;
    }

    if (file.size > MAX_DATA_URL_PREVIEW_BYTES) {
      setPreviewBlocked(true);
      return undefined;
    }

    const reader = new FileReader();
    let cancelled = false;

    reader.onload = () => {
      if (cancelled) return;
      const result = reader.result;
      setDataUrl(typeof result === "string" ? result : null);
    };
    reader.onerror = () => {
      if (!cancelled) {
        setDataUrl(null);
        setPreviewBlocked(true);
      }
    };
    reader.readAsDataURL(file);

    return () => {
      cancelled = true;
    };
  }, [imageKind, file]);

  if (metaSrc && !metaImgFailed) {
    return (
      <div
        className={`document-file-preview document-file-preview--image ${className}`.trim()}
      >
        <img
          src={metaSrc}
          alt=""
          className="document-file-preview__img"
          onError={() => setMetaImgFailed(true)}
        />
      </div>
    );
  }


  
  const name =
    (file && typeof file === "object" && !(file instanceof Blob)
      ? file.name || file.fileName
      : file?.name) || "Document";
  const loadingPreview =
    imageKind === "image" && !dataUrl && !previewBlocked && !imgError;
  const showImage = imageKind === "image" && dataUrl && !imgError && !previewBlocked;

  if (loadingPreview) {
    return (
      <div
        className={`document-file-preview document-file-preview--loading ${className}`.trim()}
        aria-busy="true"
        aria-label="Loading preview"
      />
    );
  }

  if (showImage) {
    return (
      <div className={`document-file-preview document-file-preview--image ${className}`.trim()}>
        <img
          src={dataUrl}
          alt=""
          className="document-file-preview__img"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  const tileKind =
    (imgError || previewBlocked) && imageKind === "image" ? "generic" : iconKind;

  return (
    <div className={`document-file-preview document-file-preview--tile ${className}`.trim()}>
      <span className="document-file-preview__glyph">
        <IconByKind kind={tileKind} />
      </span>
      <span className="document-file-preview__name" title={name}>
        {name}
      </span>
    </div>
  );
}
